/**
 * Library Context
 * 
 * React Context for managing library state using useReducer.
 * Provides state and actions to all library components.
 */

import React, { createContext, useReducer, useContext, useCallback } from 'react';

// Entity type configuration
export const ENTITY_TYPES = {
  module: { key: 'module', label: 'Module', plural: 'Modules' },
  feature: { key: 'feature', label: 'Feature', plural: 'Features' },
  asset: { key: 'asset', label: 'Asset', plural: 'Assets' },
  damage_scenario: { key: 'damage_scenario', label: 'Damage Scenario', plural: 'Damage Scenarios' },
  threat: { key: 'threat', label: 'Threat', plural: 'Threats' },
  attack_path: { key: 'attack_path', label: 'Attack Path', plural: 'Attack Paths' },
  goal: { key: 'goal', label: 'Goal', plural: 'Cybersecurity Goals' },
  claim: { key: 'claim', label: 'Claim', plural: 'Cybersecurity Claims' },
  control: { key: 'control', label: 'Control', plural: 'Controls' },
  technology: { key: 'technology', label: 'Technology', plural: 'Technologies' }
};

// Initial state
const initialState = {
  currentSection: 'module',
  items: {}, // Organized by entity type
  counts: {}, // Item counts by entity type
  selectedItem: null,
  isFormOpen: false,
  editingItem: null,
  searchQuery: '',
  selectedTags: [],
  sortBy: 'updated_at',
  sortOrder: -1,
  loading: false,
  error: null,
  isExportModalOpen: false,
  isImportModalOpen: false
};

// Action types
const actionTypes = {
  SET_SECTION: 'SET_SECTION',
  SET_ITEMS: 'SET_ITEMS',
  SET_COUNTS: 'SET_COUNTS',
  ADD_ITEM: 'ADD_ITEM',
  UPDATE_ITEM: 'UPDATE_ITEM',
  DELETE_ITEM: 'DELETE_ITEM',
  SET_SELECTED_ITEM: 'SET_SELECTED_ITEM',
  OPEN_FORM: 'OPEN_FORM',
  CLOSE_FORM: 'CLOSE_FORM',
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
  SET_SELECTED_TAGS: 'SET_SELECTED_TAGS',
  SET_SORT: 'SET_SORT',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  OPEN_EXPORT_MODAL: 'OPEN_EXPORT_MODAL',
  CLOSE_EXPORT_MODAL: 'CLOSE_EXPORT_MODAL',
  OPEN_IMPORT_MODAL: 'OPEN_IMPORT_MODAL',
  CLOSE_IMPORT_MODAL: 'CLOSE_IMPORT_MODAL',
  RESET_STATE: 'RESET_STATE'
};

// Reducer function
function libraryReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_SECTION:
      return {
        ...state,
        currentSection: action.payload,
        isFormOpen: false,
        editingItem: null,
        selectedItem: null
      };

    case actionTypes.SET_ITEMS:
      return {
        ...state,
        items: {
          ...state.items,
          [action.payload.entityType]: action.payload.items
        },
        loading: false,
        error: null
      };

    case actionTypes.SET_COUNTS:
      return {
        ...state,
        counts: action.payload
      };

    case actionTypes.ADD_ITEM:
      const entityType = action.payload.entity_type;
      const currentItems = state.items[entityType] || [];
      return {
        ...state,
        items: {
          ...state.items,
          [entityType]: [action.payload, ...currentItems]
        },
        counts: {
          ...state.counts,
          [entityType]: (state.counts[entityType] || 0) + 1
        }
      };

    case actionTypes.UPDATE_ITEM:
      const updatedEntityType = action.payload.entity_type;
      const updatedItems = (state.items[updatedEntityType] || []).map(item =>
        item.item_id === action.payload.item_id ? action.payload : item
      );
      return {
        ...state,
        items: {
          ...state.items,
          [updatedEntityType]: updatedItems
        }
      };

    case actionTypes.DELETE_ITEM:
      const deleteEntityType = action.payload.entityType;
      const filteredItems = (state.items[deleteEntityType] || []).filter(
        item => item.item_id !== action.payload.itemId
      );
      return {
        ...state,
        items: {
          ...state.items,
          [deleteEntityType]: filteredItems
        },
        counts: {
          ...state.counts,
          [deleteEntityType]: Math.max(0, (state.counts[deleteEntityType] || 0) - 1)
        }
      };

    case actionTypes.SET_SELECTED_ITEM:
      return {
        ...state,
        selectedItem: action.payload
      };

    case actionTypes.OPEN_FORM:
      return {
        ...state,
        isFormOpen: true,
        editingItem: action.payload || null
      };

    case actionTypes.CLOSE_FORM:
      return {
        ...state,
        isFormOpen: false,
        editingItem: null
      };

    case actionTypes.SET_SEARCH_QUERY:
      return {
        ...state,
        searchQuery: action.payload
      };

    case actionTypes.SET_SELECTED_TAGS:
      return {
        ...state,
        selectedTags: action.payload
      };

    case actionTypes.SET_SORT:
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortOrder: action.payload.sortOrder
      };

    case actionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };

    case actionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };

    case actionTypes.OPEN_EXPORT_MODAL:
      return {
        ...state,
        isExportModalOpen: true
      };

    case actionTypes.CLOSE_EXPORT_MODAL:
      return {
        ...state,
        isExportModalOpen: false
      };

    case actionTypes.OPEN_IMPORT_MODAL:
      return {
        ...state,
        isImportModalOpen: true
      };

    case actionTypes.CLOSE_IMPORT_MODAL:
      return {
        ...state,
        isImportModalOpen: false
      };

    case actionTypes.RESET_STATE:
      return initialState;

    default:
      return state;
  }
}

// Create context
const LibraryContext = createContext(null);

// Provider component
export function LibraryProvider({ children }) {
  const [state, dispatch] = useReducer(libraryReducer, initialState);

  // Actions
  const actions = {
    setCurrentSection: useCallback((section) => {
      dispatch({ type: actionTypes.SET_SECTION, payload: section });
    }, []),

    setItems: useCallback((entityType, items) => {
      dispatch({ type: actionTypes.SET_ITEMS, payload: { entityType, items } });
    }, []),

    setCounts: useCallback((counts) => {
      dispatch({ type: actionTypes.SET_COUNTS, payload: counts });
    }, []),

    addItem: useCallback((item) => {
      dispatch({ type: actionTypes.ADD_ITEM, payload: item });
    }, []),

    updateItem: useCallback((item) => {
      dispatch({ type: actionTypes.UPDATE_ITEM, payload: item });
    }, []),

    deleteItem: useCallback((entityType, itemId) => {
      dispatch({ type: actionTypes.DELETE_ITEM, payload: { entityType, itemId } });
    }, []),

    setSelectedItem: useCallback((item) => {
      dispatch({ type: actionTypes.SET_SELECTED_ITEM, payload: item });
    }, []),

    openForm: useCallback((editItem = null) => {
      dispatch({ type: actionTypes.OPEN_FORM, payload: editItem });
    }, []),

    closeForm: useCallback(() => {
      dispatch({ type: actionTypes.CLOSE_FORM });
    }, []),

    setSearchQuery: useCallback((query) => {
      dispatch({ type: actionTypes.SET_SEARCH_QUERY, payload: query });
    }, []),

    setSelectedTags: useCallback((tags) => {
      dispatch({ type: actionTypes.SET_SELECTED_TAGS, payload: tags });
    }, []),

    setSort: useCallback((sortBy, sortOrder) => {
      dispatch({ type: actionTypes.SET_SORT, payload: { sortBy, sortOrder } });
    }, []),

    setLoading: useCallback((loading) => {
      dispatch({ type: actionTypes.SET_LOADING, payload: loading });
    }, []),

    setError: useCallback((error) => {
      dispatch({ type: actionTypes.SET_ERROR, payload: error });
    }, []),

    openExportModal: useCallback(() => {
      dispatch({ type: actionTypes.OPEN_EXPORT_MODAL });
    }, []),

    closeExportModal: useCallback(() => {
      dispatch({ type: actionTypes.CLOSE_EXPORT_MODAL });
    }, []),

    openImportModal: useCallback(() => {
      dispatch({ type: actionTypes.OPEN_IMPORT_MODAL });
    }, []),

    closeImportModal: useCallback(() => {
      dispatch({ type: actionTypes.CLOSE_IMPORT_MODAL });
    }, []),

    resetState: useCallback(() => {
      dispatch({ type: actionTypes.RESET_STATE });
    }, [])
  };

  const value = { state, actions };

  return (
    <LibraryContext.Provider value={value}>
      {children}
    </LibraryContext.Provider>
  );
}

// Custom hook to use library context
export function useLibrary() {
  const context = useContext(LibraryContext);
  
  if (!context) {
    throw new Error('useLibrary must be used within a LibraryProvider');
  }
  
  return context;
}

export default LibraryContext;
